import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"
import i18n from "../i18n";
import { withUserZone } from "./oidc";

export { withUserZone };

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Dates shown to the user follow the UI language (FR/EN switch), never a hardcoded 'fr-FR'.
 * Filenames are the exception: formatFilenameStamp stays language-independent.
 */
export function intlLocale(lang = i18n.language) {
  return String(lang || "fr").startsWith("en") ? "en-US" : "fr-FR";
}

function toValidDate(value) {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date only, e.g. formatDate(iso, { day: "numeric", month: "short" }). Empty/invalid → "". */
export function formatDate(value, options) {
  const d = toValidDate(value);
  return d ? d.toLocaleDateString(intlLocale(), withUserZone(options)) : "";
}

/** Date + time. Empty/invalid → "". */
export function formatDateTime(value, options) {
  const d = toValidDate(value);
  return d ? d.toLocaleString(intlLocale(), withUserZone(options)) : "";
}

/** Time only, e.g. formatTime(iso, { hour: "2-digit", minute: "2-digit" }). Empty/invalid → "". */
export function formatTime(value, options) {
  const d = toValidDate(value);
  return d ? d.toLocaleTimeString(intlLocale(), withUserZone(options)) : "";
}

/**
 * Group assets by relative time period for Apple Photos-style sectioning.
 * Input: assets sorted by created_at desc (most recent first).
 * Returns: [{ label, items }, ...] preserving chronological order.
 * Buckets: Aujourd'hui / Hier / Cette semaine / Ce mois-ci / "Mois Année" (older),
 * in the UI language.
 */
export function bucketLabelOfDate(rawDate, t = (key) => i18n.t(key)) {
  const ts = rawDate ? new Date(rawDate).getTime() : 0;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86400000;
  const weekAgo = today - 6 * 86400000;
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  if (ts >= today) return t("time.today");
  if (ts >= yesterday) return t("time.yesterday");
  if (ts >= weekAgo) return t("time.thisWeek");
  if (ts >= monthStart) return t("time.thisMonth");
  const m = formatDate(ts, { month: "long", year: "numeric" });
  return m ? m.charAt(0).toUpperCase() + m.slice(1) : "—";
}

/**
 * Day-level label for search/history grouping: "Aujourd'hui", "Hier", "Lundi 20 octobre",
 * "Mardi 14 octobre 2025" (year only if ≠ current year). Missing date → unknown date label.
 * Follows the UI language (FR/EN switch), never hardcoded 'fr-FR'.
 */
export function dayLabelOfDate(rawDate, now = new Date(), t = (key) => i18n.t(key)) {
  const d = rawDate ? new Date(rawDate) : null;
  if (!d || Number.isNaN(d.getTime())) return t("time.unknownDate");
  const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, now)) return t("time.today");
  if (sameDay(d, new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1))) return t("time.yesterday");
  const opts = { weekday: "long", day: "numeric", month: "long" };
  if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  const s = d.toLocaleDateString(intlLocale(), opts);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function groupAssetsByDate(assets, getDate = (a) => a.created_at, t = undefined) {
  if (!Array.isArray(assets) || assets.length === 0) return [];
  const buckets = new Map();
  for (const asset of assets) {
    const label = dayLabelOfDate(getDate(asset), new Date(), t);
    if (!buckets.has(label)) buckets.set(label, []);
    buckets.get(label).push(asset);
  }
  return Array.from(buckets, ([label, items]) => ({ label, items }));
}

/**
 * Fixed period chips shared by HistoryPanel + LibraryPanel (label: t(chip.labelKey)).
 * Server-side date filtering: each chip maps to a created_at range query.
 */
export const PERIOD_CHIPS = [
  { id: "all", labelKey: "time.all" },
  { id: "today", labelKey: "time.today" },
  { id: "yesterday", labelKey: "time.yesterday" },
  { id: "7d", labelKey: "time.last7Days" },
  { id: "30d", labelKey: "time.last30Days" },
  { id: "90d", labelKey: "time.last90Days" },
];

export const isCustomPeriod = (period) => !!period && typeof period === "object";

/** "2026-10-14" → minuit LOCAL de ce jour (new Date("2026-10-14") serait minuit UTC). null si illisible. */
function localDayStart(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || "");
  if (!m) return null;
  const d = new Date(2000, 0, 1);
  d.setFullYear(Number(m[1]), Number(m[2]) - 1, Number(m[3])); // setFullYear : l'an 0020 tapé reste 0020 (pas 1920)
  return d;
}

/** Date → "YYYY-MM-DD" du jour LOCAL (valeur d'un <input type="date">). */
export function toDayInput(date = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

/**
 * Période → { date_from, date_to } ISO (date_to exclusive) pour le backend. Bornes calculées en
 * heure LOCALE (comme dayLabelOfDate), jours de calendrier (sûr au changement d'heure), puis
 * converties en UTC via toISOString(). {} pour "all" ou une période illisible.
 */
export function dateRangeForPeriod(period, now = new Date()) {
  if (!period || period === "all") return {};
  const day = (offset) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
  if (isCustomPeriod(period)) {
    let from = localDayStart(period.from);
    let to = localDayStart(period.to);
    if (from && to && from > to) [from, to] = [to, from];
    const out = {};
    if (from) out.date_from = from.toISOString();
    if (to) {
      const end = new Date(to);
      end.setDate(end.getDate() + 1);   // pas new Date(y, m, d+1) : l'an 0020 tapé serait relu 1920
      out.date_to = end.toISOString();
    }
    return out;
  }
  if (period === "yesterday") return { date_from: day(-1).toISOString(), date_to: day(0).toISOString() };
  const days = { today: 1, "7d": 7, "30d": 30, "90d": 90 }[period];
  if (!days) return {};
  return { date_from: day(-(days - 1)).toISOString(), date_to: day(1).toISOString() };
}

/**
 * Recherche texte côté client, MÊME règle que le serveur (backend/media_search.py) : chaque mot
 * tapé doit apparaître dans l'un des textes, ordre libre, sans casse ni accents. Terme vide → true.
 */
const foldText = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
export function matchesSearch(term, ...texts) {
  const words = foldText(term).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const blob = texts.map(foldText).join("\n");
  return words.every((w) => blob.includes(w));
}

/**
 * Prompt à montrer / copier pour un média de /library/media : celui de SA génération quand le serveur le
 * connaît (image de plan : image_history[].prompt → `generation_prompt`), sinon le prompt actuel du plan /
 * de l'asset. Le marqueur interne « [EDIT] » d'une édition de plan (routes/image_gen.py) est retiré.
 */
export function mediaPromptOf(item) {
  const p = (item && (item.generation_prompt || item.prompt)) || "";
  return typeof p === "string" ? p.replace(/^\[EDIT\]\s*/, "") : "";
}

/** Résumé lisible de la période active (« du 14 oct. au 20 oct. »), "" pour "all". */
export function periodSummary(period) {
  if (!period || period === "all") return "";
  if (!isCustomPeriod(period)) {
    return { today: "aujourd'hui", yesterday: "hier", "7d": "7 derniers jours", "30d": "30 derniers jours", "90d": "90 derniers jours" }[period] || "";
  }
  const fmt = (ymd) => {
    const d = localDayStart(ymd);
    if (!d) return "";
    const opts = { day: "numeric", month: "short" };
    if (d.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";
    return d.toLocaleDateString(intlLocale(), opts);
  };
  let { from, to } = period;
  if (from && to && from > to) [from, to] = [to, from];
  const f = fmt(from), t = fmt(to);
  if (f && t) return f === t ? `le ${f}` : `du ${f} au ${t}`;
  if (f) return `depuis le ${f}`;
  if (t) return `jusqu'au ${t}`;
  return "";
}

/**
 * Resolve a media URL (image/video) to a full URL.
 *
 * Without `w`: returns the URL unchanged (relative paths get BACKEND_URL prepended).
 * With `w` (target thumbnail width): routes through the backend thumbnail endpoint
 *   `/api/media/{path}?w=W` (cached WebP). For GCS signed URLs we strip the signed
 *   query and rebuild a backend path so the thumbnail cache is actually used —
 *   appending `?w=W` to a signed GCS URL is ignored by GCS and downloads full-res.
 *   For 3rd-party CDN URLs (fal.media, runwayml, etc.) `w` is ignored, since those
 *   hosts don't honor it and the extra param would just pollute the request.
 *
 * @param {string} url
 * @param {number} [w] - Optional thumbnail width (snaps to allowed sizes server-side)
 */
export function resolveMediaUrl(url, w) {
  if (!url || typeof url !== 'string') return '';
  if (url.startsWith('data:')) return url;

  const backendBase = process.env.REACT_APP_BACKEND_URL || '';

  // GCS signed URLs expire (~1h). Always rebuild a backend path so the request is
  // re-signed/proxied server-side — both for thumbnails (?w) AND full-res (no w),
  // otherwise a stale signed URL renders as a broken image (e.g. in the preview dialog).
  if (/^https:\/\/(?:[^/.]+\.)?storage\.googleapis\.com\//.test(url)) {
    try {
      const u = new URL(url);
      const isPathStyle = u.hostname === 'storage.googleapis.com';
      const path = isPathStyle
        ? u.pathname.replace(/^\/[^/]+\//, '')
        : u.pathname.replace(/^\//, '');
      if (path) return `${backendBase}/api/${path}${w ? `?w=${w}` : ''}`;
    } catch { /* fall through */ }
    return url;
  }

  if (w === undefined || w === null) {
    if (url.startsWith('http')) return url;
    return `${backendBase}${url}`;
  }

  if (url.startsWith('http')) return url;

  const base = `${backendBase}${url}`;
  return `${base}${base.includes('?') ? '&' : '?'}w=${w}`;
}

/**
 * Nom des fichiers téléchargés — convention unique (PLAN_NOMENCLATURE_TELECHARGEMENTS.md) :
 *   {Projet}_{AAAA-MM-JJ}_{HHhMM}_{Origine}_{vN[-outil]}_{Auteur}_{id8}.ext
 *   ORIGO_2026-09-13_14h32_Sc001-Sh003_v2-reshoot_Corpus_e5f6a7b8.mp4
 *   Toolbox_2026-09-13_14h32_edit-kling-o3_v1_Corpus_c9d0e1f2.png
 *
 * - Projet d'abord : dans le Finder, tri alphabétique = groupé par projet puis chronologique.
 * - La date est celle de l'ORIGINAL (racine de la chaîne de versions, `lineage.root_created_at`),
 *   en heure de Paris FIXE — deux coéquipiers dans deux fuseaux doivent produire le même nom.
 * - Origine : plan (`Sc001-Sh003`, multishot `Sc001-Sh001-002-003`) ou outil Toolbox + moteur.
 * - Version : `v1` = original ; `v2-reshoot` = média modifié (backend `_resolve_lineage`).
 * - Auteur : celui qui a GÉNÉRÉ, jamais celui qui télécharge. Omis s'il n'est pas connu de
 *   façon sûre — jamais un email ni le « ? » que le scope équipe pose sur un inconnu.
 * - id8 : retrouver le média exact en base depuis un fichier reçu par mail.
 * Les segments vides sont omis ; tout caractère hors [A-Za-z0-9.-] tombe (`:` interdit
 * sur macOS/Windows, le backend garde `-._` dans Content-Disposition).
 */
const FILENAME_TZ = 'Europe/Paris';
// Libellés d'outils tels que l'équipe les nomme (le reste = nom technique, `_` → `-`)
const TOOL_LABELS = { video_recut: 'reshoot', video_reframe: 'reframe', image_edit: 'edit', video_edit: 'edit', image_generate: 'image', video_gen: 'video' };

export function cleanFilenameSegment(value, max = 20) {
  const s = String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, max)
    .replace(/-+$/g, '');
  return s;
}

/** "2026-09-13_14h32" en heure de Paris. Date invalide ou absente → maintenant. */
export function formatFilenameStamp(value) {
  let d = value instanceof Date ? value : new Date(value ?? Date.now());
  if (Number.isNaN(d.getTime())) d = new Date();
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: FILENAME_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  // Intl rend parfois "24" à minuit selon le moteur → normalisé
  const hour = parts.hour === '24' ? '00' : parts.hour;
  return `${parts.year}-${parts.month}-${parts.day}_${hour}h${parts.minute}`;
}

function shotOriginSegment(sceneNumber, shotNumbers) {
  if (sceneNumber === null || sceneNumber === undefined || Number.isNaN(Number(sceneNumber))) return '';
  const shotsArr = Array.isArray(shotNumbers) ? shotNumbers : [shotNumbers];
  const cleanShots = shotsArr
    .filter(n => n !== null && n !== undefined && !Number.isNaN(Number(n)))
    .map(Number)
    .sort((a, b) => a - b)
    .map(n => String(n).padStart(3, '0'));
  const sc = `Sc${String(Number(sceneNumber)).padStart(3, '0')}`;
  return cleanShots.length ? `${sc}-Sh${cleanShots.join('-')}` : sc;
}

function toolLabel(tool) {
  const t = String(tool ?? '');
  return TOOL_LABELS[t] || cleanFilenameSegment(t.toLowerCase(), 24);
}

/** Auteur affichable dans un nom de fichier, ou '' : jamais un email, jamais « ? ». */
export function filenameAuthor(name) {
  const n = String(name ?? '').trim();
  if (!n || n === '?' || n.includes('@')) return '';
  return cleanFilenameSegment(n, 16);
}

/**
 * @param {Object} meta
 * @param {string}  [meta.projectName]  nom du projet ('' → "Toolbox" si outil, sinon "Media")
 * @param {string|Date} [meta.createdAt] date de l'ORIGINAL (ISO) — absente → maintenant
 * @param {number}  [meta.sceneNumber]  origine plan
 * @param {number|number[]} [meta.shotNumbers]
 * @param {string}  [meta.tool]         origine Toolbox (si pas de plan)
 * @param {string}  [meta.engine]
 * @param {number}  [meta.version=1]
 * @param {string}  [meta.versionTool]  outil qui a fait CETTE version (v ≥ 2)
 * @param {string}  [meta.authorName]
 * @param {string}  [meta.id]           id en base → 8 premiers caractères
 * @param {string}  [meta.ext]          avec ou sans point
 */
export function buildMediaFilename({
  projectName, createdAt, sceneNumber, shotNumbers, tool, engine,
  version, versionTool, authorName, id, ext,
  // Page Plateau (décision Corpus 2026-09-17) : `priseN[-reshoot]` remplace le segment version, et
  // l'appelant omet `id` → pas d'id8 (le nom de prise + l'heure suffisent). La Toolbox ne passe
  // pas `takeNumber` et garde sa convention intacte.
  takeNumber, takeSuffix,
} = {}) {
  const origin = shotOriginSegment(sceneNumber, shotNumbers)
    || [toolLabel(tool), cleanFilenameSegment(String(engine ?? '').toLowerCase(), 16)].filter(Boolean).join('-');
  const project = cleanFilenameSegment(projectName, 20) || (tool ? 'Toolbox' : 'Media');
  const v = Math.max(1, parseInt(version, 10) || 1);
  const takeN = parseInt(takeNumber, 10);
  const versionSeg = takeN > 0
    ? `prise${takeN}${takeSuffix ? `-${cleanFilenameSegment(String(takeSuffix), 12)}` : ''}`
    : (v > 1 && versionTool ? `v${v}-${toolLabel(versionTool)}` : `v${v}`);
  const cleanExt = String(ext ?? '').replace(/^\./, '').toLowerCase().replace(/[^a-z0-9]/g, '') || 'bin';
  const segments = [
    project,
    formatFilenameStamp(createdAt),
    origin,
    versionSeg,
    filenameAuthor(authorName),
    cleanFilenameSegment(String(id ?? '').slice(0, 8), 8),
  ].filter(Boolean);
  return `${segments.join('_')}.${cleanExt}`;
}

/** Nom d'un ZIP d'export : "ORIGO_export_2026-09-14_11h20.zip" — daté du téléchargement. */
export function buildZipName(baseName) {
  return `${cleanFilenameSegment(baseName, 20) || 'Bibliotheque'}_export_${formatFilenameStamp()}.zip`;
}

/**
 * Métadonnées de nommage d'un asset Toolbox ou d'un item Bibliothèque (mêmes champs, à
 * `raw`/`lineage` près) → entrée de buildMediaFilename. `lineage` (backend
 * `_resolve_lineage`) fait remonter date et origine à l'ORIGINAL de la chaîne.
 * @param {Object} a      asset Toolbox / item Bibliothèque
 * @param {Object} [opts]
 * @param {string} [opts.projectName]
 * @param {string} [opts.authorName]  déjà résolu par l'appelant (cf. authorNameFor)
 */
export function mediaFilenameMeta(a, { projectName, authorName } = {}) {
  const src = a || {};
  const lin = src.lineage && typeof src.lineage === 'object' ? src.lineage : null;
  const isShot = src.source === 'shot' && src.scene_number != null;
  const rootIsShot = lin?.root_kind === 'shot' && lin.root_scene_number != null;
  return {
    projectName: projectName ?? src.project_name,
    createdAt: lin?.root_created_at || src.created_at || src.timestamp || src.completed_at,
    sceneNumber: isShot ? src.scene_number : (rootIsShot ? lin.root_scene_number : undefined),
    shotNumbers: isShot ? src.shot_number : (rootIsShot ? lin.root_shot_number : undefined),
    tool: src.tool,
    engine: src.engine || src.provider,
    version: lin?.version || 1,
    versionTool: lin?.version_tool,
    authorName,
    id: src.generation_id || src.id,
    ext: guessMediaExtension(src.url || src.local_path, src.type === 'video' ? 'mp4' : (src.type === 'audio' ? 'mp3' : 'png')),
  };
}

/**
 * Auteur d'un média pour son nom de fichier. Le backend ne sert `user_name` qu'aux rôles
 * privilégiés (library.py) et le scope équipe pose « ? » sur un inconnu ; l'utilisateur
 * courant n'est l'auteur que si l'asset porte SON id. Sinon '' — on n'invente pas un auteur.
 * `user_source` (Bibliothèque) : une déduction n'est pas un fait, un nom de fichier en est un.
 */
export function authorNameFor(a, me) {
  const src = a || {};
  if (src.user_source && src.user_source !== 'recorded') return '';
  if (src.user_name) return filenameAuthor(src.user_name);
  if (me?.id && src.user_id === me.id) return filenameAuthor(me.name);
  return '';
}

/** Extension depuis l'URL (avant query string), sans point. */
export function guessMediaExtension(url, fallback = 'png') {
  const m = String(url ?? '').split('?')[0].match(/\.(jpg|jpeg|png|webp|gif|mp4|webm|mov|wav|mp3|m4a)$/i);
  return m ? m[1].toLowerCase() : fallback;
}

/**
 * Clé de comparaison entre deux URLs du même média — miroir de `entity_resolver.ref_url_key`
 * (backend) : forme relative `/api/media/...` vs absolue (prod préfixe REACT_APP_BACKEND_URL),
 * avec ou sans `?w=` de vignette. Comparer, jamais afficher ni envoyer.
 */
export function mediaUrlKey(url) {
  const s = String(url ?? '');
  const i = s.indexOf('/api/');
  return (i >= 0 ? s.slice(i) : s).split('?')[0];
}

/** L'asset Toolbox (historique) qui porte cette URL — `local_path` (notre copie) ou `url` (CDN). */
export function findAssetByUrl(assets, url) {
  const k = mediaUrlKey(url);
  if (!k) return null;
  return (assets || []).find(a => a && (mediaUrlKey(a.local_path) === k || mediaUrlKey(a.url) === k)) || null;
}

/**
 * Force-download a media file instead of opening it in a new tab.
 * Fetches as blob (same-origin then no-credentials for cross-origin CDNs).
 * Falls back to <a download> as last resort.
 */
export async function forceDownload(url, filename) {
  const resolved = resolveMediaUrl(url);
  // Backend-hosted media: ask the backend to sign the GCS URL with
  // Content-Disposition: attachment so the browser downloads instead of
  // navigating. Avoids needing CORS on the GCS bucket.
  const isBackendMedia =
    resolved.startsWith('/api/media/') ||
    /^https?:\/\/[^/]+\/api\/media\//.test(resolved);
  if (isBackendMedia) {
    const sep = resolved.includes('?') ? '&' : '?';
    const a = document.createElement('a');
    a.href = `${resolved}${sep}download=${encodeURIComponent(filename || 'download')}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  }
  const downloadBlob = (blob) => {
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 500);
  };
  // Attempt 1: default credentials (same-origin sends cookies, cross-origin does not)
  try {
    const r = await fetch(resolved, { mode: 'cors', credentials: 'same-origin' });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return downloadBlob(await r.blob());
  } catch { /* fall through */ }
  // Attempt 2: no credentials (cross-origin CDNs that reject even same-origin preflight)
  try {
    const r = await fetch(resolved, { mode: 'cors', credentials: 'omit' });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return downloadBlob(await r.blob());
  } catch { /* fall through */ }
  // Last resort: <a download> (browser may ignore for cross-origin)
  const a = document.createElement('a');
  a.href = resolved;
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
